import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Flame, ArrowRight } from "lucide-react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import statesTopo from "us-atlas/states-10m.json";

const fillStyle = (pct, remaining) => {
  if (remaining <= 0) return { bar: "bg-slate-400", ring: "border-slate-300", label: "Sold out", text: "text-slate-500", hex: "#94a3b8" };
  if (pct >= 67) return { bar: "bg-sam-crimson", ring: "border-sam-crimson/40", label: "Filling fast", text: "text-sam-crimson", hex: "#c0392b" };
  if (pct >= 34) return { bar: "bg-sam-amber", ring: "border-sam-amber/40", label: "Going", text: "text-sam-amber", hex: "#e0a106" };
  return { bar: "bg-sam-green", ring: "border-sam-green/30", label: "Open", text: "text-sam-green", hex: "#2f8f4e" };
};

export default function Coverage() {
  const [markets, setMarkets] = useState([]);
  const [status, setStatus] = useState("loading");
  const [hovered, setHovered] = useState(null);
  const navigate = useNavigate();
  const pinned = useMemo(() => markets.filter((m) => m.lat && m.lng), [markets]);

  useEffect(() => {
    api.get("/coverage-map").then(({ data }) => { setMarkets(data.markets); setStatus("ok"); }).catch(() => setStatus("ok"));
    document.title = "Mailer coverage & availability | Saving America Money";
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="coverage-page">
      <div className="rounded-3xl bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream p-8 sm:p-12 mb-10">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-amber"><Flame className="h-4 w-4" />Live availability</span>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-black">Where the mailers are filling up</h1>
        <p className="mt-4 text-sam-cream/75 max-w-2xl text-base">Each premium co-op mailer holds just 12 ad spaces per ZIP. Grab yours before a competitor claims the spot.</p>
        <div className="mt-6 flex flex-wrap gap-4 text-xs">
          {[["Open", "bg-sam-green"], ["Going", "bg-sam-amber"], ["Filling fast", "bg-sam-crimson"], ["Sold out", "bg-slate-400"]].map(([l, c]) => (
            <span key={l} className="inline-flex items-center gap-1.5 text-sam-cream/80"><span className={`h-2.5 w-2.5 rounded-full ${c}`} />{l}</span>
          ))}
        </div>
      </div>

      {status === "loading" ? (
        <div className="h-40 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>
      ) : markets.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-sam-slate">No mailer markets published yet — check back soon.</p>
          <Button asChild className="mt-4 bg-sam-crimson hover:bg-sam-crimson/90"><Link to="/plans">See plans &amp; pricing</Link></Button>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-white overflow-hidden mb-6 p-2" data-testid="coverage-map">
            <ComposableMap projection="geoAlbersUsa" width={900} height={500} style={{ width: "100%", height: "auto" }}>
              <Geographies geography={statesTopo}>
                {({ geographies }) => geographies.map((geo) => (
                  <Geography key={geo.rsmKey} geography={geo} fill="#eef1f4" stroke="#cdd5df" strokeWidth={0.5} style={{ default: { outline: "none" }, hover: { outline: "none", fill: "#e2e8f0" }, pressed: { outline: "none" } }} />
                ))}
              </Geographies>
              {pinned.map((m) => {
                const cc = fillStyle(m.fill_pct, m.remaining);
                const isH = hovered === m.path;
                const label = `${m.city}, ${m.state_abbr} · ${m.remaining > 0 ? `${m.remaining} open` : "Full"}`;
                const w = label.length * 6.2 + 16;
                return (
                  <Marker key={m.path} coordinates={[m.lng, m.lat]} onClick={() => navigate(`/${m.path}`)}
                    onMouseEnter={() => setHovered(m.path)} onMouseLeave={() => setHovered(null)}
                    style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" } }}>
                    <circle r={isH ? 9 : 6.5} fill={cc.hex} stroke="#fff" strokeWidth={1.5} />
                    {isH && (
                      <g transform="translate(0,-14)" style={{ pointerEvents: "none" }}>
                        <rect x={-w / 2} y={-19} width={w} height={20} rx={5} fill="#0f2740" opacity={0.96} />
                        <text x={0} y={-5} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={600}>{label}</text>
                      </g>
                    )}
                  </Marker>
                );
              })}
            </ComposableMap>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coverage-grid">
          {markets.map((m) => {
            const c = fillStyle(m.fill_pct, m.remaining);
            return (
              <Link key={m.path} to={`/${m.path}`} data-testid={`coverage-market-${m.zip}`}
                className={`block rounded-2xl border-2 bg-card p-5 hover:shadow-lg transition-shadow ${c.ring}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-display font-bold text-sam-navy flex items-center gap-1.5"><MapPin className="h-4 w-4 text-sam-crimson" />{m.city}, {m.state_abbr}</div>
                  <span className={`text-xs font-bold uppercase ${c.text}`}>{c.label}</span>
                </div>
                <div className="text-xs text-sam-slate mt-0.5">ZIP {m.zip}</div>
                <div className="mt-4 h-2.5 rounded-full bg-sam-navy/10 overflow-hidden">
                  <div className={`h-full ${c.bar}`} style={{ width: `${Math.max(6, m.fill_pct)}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-sam-slate">{m.booked} of {m.cap} booked</span>
                  <span className="font-semibold text-sam-navy inline-flex items-center gap-1">{m.remaining > 0 ? `${m.remaining} open` : "Full"} <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}
